import React, { useState, useCallback } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { 
  Upload, Bot, FileText, TrendingUp, PackageCheck, 
  MessageSquare, FileSignature, CheckCircle, AlertCircle,
  Loader, X, ChevronRight, Eye, Download
} from 'lucide-react';

const AIAssistants = () => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  // Agent configurations
  const agents = [
    {
      id: 'compliance',
      name: 'Compliance Assistant',
      icon: FileText,
      color: 'blue',
      description: 'RAG-powered Q&A for regulatory documents (CSRD, ESRS, SFDR)',
      features: ['Answer compliance questions', 'Gap analysis', 'Citation sources'],
      inputType: 'question',
      placeholder: 'Ask a compliance question (e.g., "What are ESRS E1 requirements?")'
    },
    {
      id: 'esg_reports',
      name: 'ESG Reporting Co-Pilot',
      icon: TrendingUp,
      color: 'green',
      description: 'Generate ESRS-compliant report sections from KPI data',
      features: ['Auto-generate narratives', 'KPI contextualization', 'Export reports'],
      inputType: 'kpi',
      placeholder: 'Upload Excel/CSV with KPI data'
    },
    {
      id: 'vendor_risk',
      name: 'Vendor Risk Summarizer',
      icon: PackageCheck,
      color: 'orange',
      description: 'Analyze vendor audits and generate risk summaries',
      features: ['Risk flags 🔴🟡🟢', 'Vendor comparison', 'Due diligence reports'],
      inputType: 'vendor',
      placeholder: 'Enter vendor name to analyze'
    },
    {
      id: 'quality',
      name: 'Product Quality Inspector',
      icon: Eye,
      color: 'purple',
      description: 'AI-powered visual defect detection using GPT-4 Vision',
      features: ['Defect classification', 'Quality scores', 'Root cause analysis'],
      inputType: 'image',
      placeholder: 'Upload product images for inspection'
    },
    {
      id: 'support',
      name: 'Customer Support Generator',
      icon: MessageSquare,
      color: 'pink',
      description: 'Generate professional support responses from ticket history',
      features: ['Multi-language support', 'Tone control', 'Auto-categorization'],
      inputType: 'ticket',
      placeholder: 'Paste customer ticket description'
    },
    {
      id: 'contracts',
      name: 'Contract Intelligence',
      icon: FileSignature,
      color: 'indigo',
      description: 'Extract clauses and identify risks in legal contracts',
      features: ['Clause extraction', 'Risk scoring', 'Alternative suggestions'],
      inputType: 'contract',
      placeholder: 'Enter contract name to analyze'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100',
      green: 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100',
      orange: 'border-orange-500 bg-orange-50 text-orange-700 hover:bg-orange-100',
      purple: 'border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100',
      pink: 'border-pink-500 bg-pink-50 text-pink-700 hover:bg-pink-100',
      indigo: 'border-indigo-500 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
    };
    return colors[color] || colors.blue;
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, []);

  const handleFileUpload = async (files) => {
    if (!selectedAgent) {
      setError('Please select an AI agent first');
      return;
    }

    setLoading(true);
    setError('');
    const uploadedFilesList = [];

    try {
      for (let file of files) {
        const reader = new FileReader();
        
        await new Promise((resolve, reject) => {
          reader.onload = async (e) => {
            const base64 = e.target.result.split(',')[1];

            const data = await apiFetch(`${API}/ai/upload`, {
              method: 'POST',
              body: {
                filename: file.name,
                agent_type: selectedAgent.id,
                content_base64: base64
              }
            });

            if (data?._error) {
              reject(new Error(data.message || 'Failed to upload files'));
              return;
            }

            uploadedFilesList.push({
              name: file.name,
              id: data.file_id,
              chunks: data.chunks_processed
            });

            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      
      setUploadedFiles(prev => [...prev, ...uploadedFilesList]);
      setResults({
        type: 'upload',
        data: { files: uploadedFilesList, message: 'Files uploaded successfully! You can now use the agent features.' }
      });
    } catch (err) {
      setError(err?.message || 'Failed to upload files');
    } finally {
      setLoading(false);
    }
  };

  const handleAgentAction = async (inputValue) => {
    if (!selectedAgent) return;
    
    setLoading(true);
    setError('');
    setResults(null);

    let data;

    switch (selectedAgent.id) {
      case 'compliance':
        data = await apiFetch(`${API}/ai/compliance/ask`, {
          method: 'POST',
          body: {
            question: inputValue,
            n_context_docs: 5
          }
        });
        if (data?._error) {
          setError(data.message || 'Failed to process request');
          break;
        }
        setResults({ type: 'compliance', data });
        break;

      case 'esg_reports':
        data = await apiFetch(`${API}/ai/esg-copilot/generate`, {
          method: 'POST',
          body: {
            section_type: 'E1_climate',
            kpi_data: { 'Scope 1 Emissions': '1250 tCO2e', 'Scope 2 Emissions': '890 tCO2e' },
            company_context: inputValue
          }
        });
        if (data?._error) {
          setError(data.message || 'Failed to process request');
          break;
        }
        setResults({ type: 'esg_report', data });
        break;

      case 'vendor_risk':
        data = await apiFetch(`${API}/ai/vendor-risk/analyze`, {
          method: 'POST',
          body: {
            vendor_name: inputValue,
            document_type: 'audit_report'
          }
        });
        if (data?._error) {
          setError(data.message || 'Failed to process request');
          break;
        }
        setResults({ type: 'vendor', data });
        break;

      case 'support':
        data = await apiFetch(`${API}/ai/support/generate`, {
          method: 'POST',
          body: {
            ticket_description: inputValue,
            tone: 'professional',
            language: 'English'
          }
        });
        if (data?._error) {
          setError(data.message || 'Failed to process request');
          break;
        }
        setResults({ type: 'support', data });
        break;

      case 'contracts':
        data = await apiFetch(`${API}/ai/contract/analyze`, {
          method: 'POST',
          body: {
            contract_name: inputValue,
            contract_type: 'vendor'
          }
        });
        if (data?._error) {
          setError(data.message || 'Failed to process request');
          break;
        }
        setResults({ type: 'contract', data });
        break;

      default:
        setError('Agent action not implemented');
    }

    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg p-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Bot size={36} />
          <div>
            <h1 className="text-3xl font-bold">AI-Powered Business Assistants</h1>
            <p className="text-indigo-100">Upload documents, analyze data, and get instant AI-powered insights</p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Upload size={24} className="text-indigo-600" />
          Upload Documents & Data
        </h2>
        
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive ? 'border-indigo-600 bg-indigo-50' : 'border-slate-300 bg-slate-50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files)}
            disabled={!selectedAgent}
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload size={48} className="mx-auto text-slate-400 mb-3" />
            <p className="text-lg font-semibold text-slate-700 mb-1">
              {selectedAgent ? `Drop files for ${selectedAgent.name}` : 'Select an AI agent first'}
            </p>
            <p className="text-sm text-slate-500">
              Supports: PDF, DOCX, CSV, Excel, Images (PNG, JPG)
            </p>
          </label>
        </div>

        {uploadedFiles.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Uploaded Files ({uploadedFiles.length})</h3>
            <div className="space-y-2">
              {uploadedFiles.slice(-5).map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} className="text-green-600" />
                    <span className="text-sm font-medium text-slate-700">{file.name}</span>
                    <span className="text-xs text-slate-500">({file.chunks} chunks)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => {
          const Icon = agent.icon;
          const isSelected = selectedAgent?.id === agent.id;
          
          return (
            <div
              key={agent.id}
              onClick={() => setSelectedAgent(agent)}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                isSelected 
                  ? `${getColorClasses(agent.color)} shadow-lg scale-105` 
                  : 'border-slate-200 bg-white hover:shadow-md'
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg ${
                  isSelected ? 'bg-white' : `bg-${agent.color}-100`
                }`}>
                  <Icon size={24} className={isSelected ? `text-${agent.color}-600` : 'text-slate-600'} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 mb-1">{agent.name}</h3>
                  <p className="text-sm text-slate-600">{agent.description}</p>
                </div>
              </div>
              
              <div className="space-y-1 mb-3">
                {agent.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-slate-600">
                    <ChevronRight size={14} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              {isSelected && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
                    <CheckCircle size={14} />
                    SELECTED
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action Panel */}
      {selectedAgent && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Bot size={24} className="text-indigo-600" />
            {selectedAgent.name} - Take Action
          </h2>
          
          <AgentActionPanel 
            agent={selectedAgent}
            onSubmit={handleAgentAction}
            loading={loading}
          />
        </div>
      )}

      {/* Results Panel */}
      {results && (
        <ResultsPanel results={results} onClose={() => setResults(null)} />
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError('')} className="ml-auto">
            <X size={18} className="text-red-600" />
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4">
            <Loader size={48} className="animate-spin text-indigo-600" />
            <p className="text-lg font-semibold text-slate-700">Processing with AI...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Agent Action Panel Component
const AgentActionPanel = ({ agent, onSubmit, loading }) => {
  const [inputValue, setInputValue] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSubmit(inputValue);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {agent.inputType === 'question' && (
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={agent.placeholder}
          className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows={4}
          disabled={loading}
        />
      )}
      
      {(agent.inputType === 'vendor' || agent.inputType === 'contract' || agent.inputType === 'ticket') && (
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={agent.placeholder}
          className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          disabled={loading}
        />
      )}
      
      <button
        type="submit"
        disabled={loading || !inputValue.trim()}
        className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Processing...' : `Ask ${agent.name}`}
      </button>
    </form>
  );
};

// Results Panel Component
const ResultsPanel = ({ results, onClose }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <CheckCircle size={24} className="text-green-600" />
          Results
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X size={20} />
        </button>
      </div>
      
      <div className="prose prose-sm max-w-none">
        {results.type === 'upload' && (
          <div>
            <p className="text-green-700 font-semibold mb-2">{results.data.message}</p>
            <ul className="list-disc pl-5">
              {results.data.files.map((file, idx) => (
                <li key={idx}>{file.name} - {file.chunks} chunks processed</li>
              ))}
            </ul>
          </div>
        )}
        
        {results.type === 'compliance' && (
          <div>
            <div className="mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                results.data.confidence === 'high' ? 'bg-green-100 text-green-700' :
                results.data.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                Confidence: {results.data.confidence}
              </span>
            </div>
            <div className="whitespace-pre-wrap text-slate-700">{results.data.answer}</div>
            {results.data.citations && results.data.citations.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Sources:</h3>
                <ul className="space-y-1">
                  {results.data.citations.map((cite, idx) => (
                    <li key={idx} className="text-sm text-blue-700">
                      📄 {cite.source} (Relevance: {(cite.relevance_score * 100).toFixed(0)}%)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {(results.type === 'esg_report' || results.type === 'vendor' || results.type === 'support' || results.type === 'contract') && (
          <div className="whitespace-pre-wrap text-slate-700">
            {results.data.content || results.data.assessment || results.data.response || results.data.analysis || JSON.stringify(results.data, null, 2)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistants;
